import { v, type Infer } from "convex/values";

/** Project roles, lowest first. The order is the hierarchy. */
export const PROJECT_ROLES = ["viewer", "member", "admin", "owner"] as const;
export const projectRoleValidator = v.union(
  v.literal("viewer"),
  v.literal("member"),
  v.literal("admin"),
  v.literal("owner"),
);
export type ProjectRole = Infer<typeof projectRoleValidator>;

/** Workspace roles are Better Auth's organization roles, lowest first. */
export const WORKSPACE_ROLES = ["member", "admin", "owner"] as const;
export const workspaceRoleValidator = v.union(
  v.literal("member"),
  v.literal("admin"),
  v.literal("owner"),
);
export type WorkspaceRole = Infer<typeof workspaceRoleValidator>;

/**
 * What a person lets an agent do with their token: read what they can read,
 * or also write what they can write. Never more than the person themselves.
 */
export const agentAccessValidator = v.union(v.literal("read"), v.literal("write"));
export type AgentAccess = Infer<typeof agentAccessValidator>;

/**
 * What an agent account runs in, which is the logo it is shown with. One of
 * a known few: the app carries their logos, and an agent that is none of
 * them is a robot.
 */
export const HARNESSES = [
  "claude_code",
  "codex",
  "t3code",
  "cursor",
  "gemini",
  "copilot",
  "windsurf",
  "opencode",
  "zed",
] as const;
export const harnessValidator = v.union(...HARNESSES.map((harness) => v.literal(harness)));
export type Harness = (typeof HARNESSES)[number];

/** Mirrors `LANGUAGES` in src/i18n/languages.ts. */
export const LANGUAGE_CODES = ["en", "fr"] as const;
export const languageValidator = v.union(v.literal("en"), v.literal("fr"));
export type Language = Infer<typeof languageValidator>;

/** Map a raw locale ("fr-FR", "FR") onto a supported language. */
export function coerceLanguage(raw: string | undefined | null): Language | undefined {
  if (!raw) return undefined;
  const code = raw.trim().toLowerCase().slice(0, 2);
  return (LANGUAGE_CODES as readonly string[]).includes(code)
    ? (code as Language)
    : undefined;
}

/** Plans, smallest first. A workspace with none set is on `free`. */
export const planValidator = v.union(
  v.literal("free"),
  v.literal("pro"),
  v.literal("ultra"),
);
export type Plan = Infer<typeof planValidator>;

export const TICKET_CATEGORIES = ["bug", "feature", "question", "praise"] as const;
export const ticketCategoryValidator = v.union(
  v.literal("bug"),
  v.literal("feature"),
  v.literal("question"),
  v.literal("praise"),
);
export type TicketCategory = Infer<typeof ticketCategoryValidator>;

/**
 * Where a ticket came from: the in-app SDK, a server calling the API, the
 * portal form, a teammate who wrote it down by hand, or a pull request
 * opening on GitHub (a step of the project's workflow).
 */
export const ticketSourceValidator = v.union(
  v.literal("sdk"),
  v.literal("api"),
  v.literal("portal"),
  v.literal("manual"),
  v.literal("github"),
);
export type TicketSource = Infer<typeof ticketSourceValidator>;

/** `triage` → `open` → `waiting` (on the requester) → `resolved`, plus two ways out. */
export const ticketStatusValidator = v.union(
  v.literal("triage"),
  v.literal("open"),
  v.literal("waiting"),
  v.literal("resolved"),
  v.literal("declined"),
  v.literal("duplicate"),
);
export type TicketStatus = Infer<typeof ticketStatusValidator>;

export const priorityValidator = v.union(
  v.literal("none"),
  v.literal("low"),
  v.literal("medium"),
  v.literal("high"),
  v.literal("urgent"),
);
export type Priority = Infer<typeof priorityValidator>;

/** The groups of the Tickets page: every status but triage, which has its own page. */
export const LISTED_TICKET_STATUSES = [
  "open",
  "waiting",
  "resolved",
  "declined",
  "duplicate",
] as const;
export const listedTicketStatusValidator = v.union(
  v.literal("open"),
  v.literal("waiting"),
  v.literal("resolved"),
  v.literal("declined"),
  v.literal("duplicate"),
);
export type ListedTicketStatus = Infer<typeof listedTicketStatusValidator>;

/**
 * The statuses a ticket is given by hand. `triage` is where a ticket starts
 * and `duplicate` needs the ticket it duplicates, so each has its own action.
 */
export const SETTABLE_TICKET_STATUSES = ["open", "waiting", "resolved", "declined"] as const;
export const settableTicketStatusValidator = v.union(
  v.literal("open"),
  v.literal("waiting"),
  v.literal("resolved"),
  v.literal("declined"),
);
export type SettableTicketStatus = Infer<typeof settableTicketStatusValidator>;

/** The fixed palette a label picks from. The client maps each to its tokens. */
export const LABEL_COLORS = [
  "red",
  "orange",
  "amber",
  "green",
  "blue",
  "violet",
  "gray",
] as const;
export const labelColorValidator = v.union(
  v.literal("red"),
  v.literal("orange"),
  v.literal("amber"),
  v.literal("green"),
  v.literal("blue"),
  v.literal("violet"),
  v.literal("gray"),
);
export type LabelColor = Infer<typeof labelColorValidator>;

/** A `reply` goes to the requester. A `note` never leaves the team. */
export const ticketMessageKindValidator = v.union(v.literal("reply"), v.literal("note"));
export type TicketMessageKind = Infer<typeof ticketMessageKindValidator>;

/** Who wrote a message: someone on the team, or the requester answering. */
export const ticketMessageAuthorValidator = v.union(
  v.object({ kind: v.literal("member"), userId: v.string() }),
  v.object({ kind: v.literal("contact"), contactId: v.id("contacts") }),
);
export type TicketMessageAuthor = Infer<typeof ticketMessageAuthorValidator>;

/**
 * Where the email of a reply stands: `pending` until Resend has taken it,
 * `sent` once it has, `delivered` when the requester's mail server accepted
 * it, `failed` when it did not get there, and `not_sent` when there was
 * nowhere to send it. A reply is a reply whichever it is.
 */
export const deliveryStateValidator = v.union(
  v.literal("pending"),
  v.literal("sent"),
  v.literal("delivered"),
  v.literal("failed"),
  v.literal("not_sent"),
);
export type DeliveryState = Infer<typeof deliveryStateValidator>;

/** Why a reply was `not_sent` (the first four) or `failed` (the last four). */
export const deliveryReasonValidator = v.union(
  v.literal("noEmail"),
  v.literal("noContact"),
  v.literal("notConfigured"),
  // The workspace has emailed more replies than it may in an hour.
  v.literal("rateLimited"),
  v.literal("bounced"),
  v.literal("complained"),
  v.literal("error"),
  // The requester's address is not one an email can be sent to.
  v.literal("invalidAddress"),
);
export type DeliveryReason = Infer<typeof deliveryReasonValidator>;

/** A reply's delivery as stored on its message. */
export const ticketMessageDeliveryValidator = v.object({
  state: deliveryStateValidator,
  reason: v.optional(deliveryReasonValidator),
  // The email's id in the Resend component, once it has been handed over.
  emailId: v.optional(v.string()),
});
export type TicketMessageDelivery = Infer<typeof ticketMessageDeliveryValidator>;

/**
 * One entry of a ticket's audit log, as stored. Structured, never a sentence:
 * the client renders and translates it. A label is recorded with the name and
 * colour it had, so the entry still reads after the label is deleted.
 */
export const activityEventValidator = v.union(
  v.object({ kind: v.literal("created") }),
  v.object({
    kind: v.literal("status_changed"),
    from: ticketStatusValidator,
    to: ticketStatusValidator,
  }),
  v.object({
    kind: v.literal("priority_changed"),
    from: priorityValidator,
    to: priorityValidator,
  }),
  // `to` is a userId, or null when the ticket was left with nobody.
  v.object({ kind: v.literal("assigned"), to: v.union(v.string(), v.null()) }),
  v.object({
    kind: v.literal("label_added"),
    labelId: v.id("labels"),
    name: v.string(),
    color: labelColorValidator,
  }),
  v.object({
    kind: v.literal("label_removed"),
    labelId: v.id("labels"),
    name: v.string(),
    color: labelColorValidator,
  }),
  v.object({ kind: v.literal("snoozed"), until: v.number() }),
  v.object({ kind: v.literal("unsnoozed") }),
  v.object({ kind: v.literal("marked_duplicate"), of: v.id("tickets") }),
);
export type ActivityEvent = Infer<typeof activityEventValidator>;

/**
 * What the inbox can tell someone about a ticket: it was assigned to them, or
 * the requester of one they look after has answered.
 */
export const ticketNotificationKindValidator = v.union(
  v.literal("assigned"),
  v.literal("requester_replied"),
  // An agent of theirs wrote an answer to the requester, waiting for them.
  v.literal("reply_drafted"),
);

/**
 * And about an issue: it was assigned to them, or a comment on it names them.
 */
export const issueNotificationKindValidator = v.union(
  v.literal("issue_assigned"),
  v.literal("mentioned"),
  // An agent of theirs was given the issue, and T3 Code could not be told.
  v.literal("agent_handoff_failed"),
  // To those subscribed to it (lib/issueSubscriptions.ts): someone commented
  // on it, or changed its status or its assignee.
  v.literal("issue_commented"),
  v.literal("issue_changed"),
);

/** How a project is going, as an update of it says (projectUpdateTables.ts). */
export const PROJECT_HEALTHS = ["on_track", "at_risk", "off_track"] as const;
export const projectHealthValidator = v.union(
  v.literal("on_track"),
  v.literal("at_risk"),
  v.literal("off_track"),
);
export type ProjectHealth = Infer<typeof projectHealthValidator>;

/** What a document is: words in Markdown, or a draw.io diagram (documentTables.ts). */
export const DOCUMENT_KINDS = ["text", "diagram"] as const;
export const documentKindValidator = v.union(v.literal("text"), v.literal("diagram"));
export type DocumentKind = Infer<typeof documentKindValidator>;

/** Every kind of notification: what a row of the inbox can be. */
export const notificationKindValidator = v.union(
  v.literal("assigned"),
  v.literal("requester_replied"),
  v.literal("reply_drafted"),
  v.literal("issue_assigned"),
  v.literal("mentioned"),
  v.literal("agent_handoff_failed"),
  // A teammate posted an update on a project they can open.
  v.literal("project_update"),
  v.literal("issue_commented"),
  v.literal("issue_changed"),
);
export type NotificationKind = Infer<typeof notificationKindValidator>;

/** In an assignee filter, the value that stands for "assigned to nobody". */
export const UNASSIGNED = "none";

/**
 * The filters of the Tickets page, which are also what a saved view stores.
 * Every kind is optional. Kinds combine with AND, the values of one kind with
 * OR, and an empty list is the same as no list.
 */
export const ticketFiltersValidator = v.object({
  category: v.optional(v.array(ticketCategoryValidator)),
  priority: v.optional(v.array(priorityValidator)),
  // User ids, and `UNASSIGNED`.
  assigneeId: v.optional(v.array(v.string())),
  labelId: v.optional(v.array(v.id("labels"))),
  contactId: v.optional(v.id("contacts")),
  source: v.optional(v.array(ticketSourceValidator)),
});
export type TicketFilters = Infer<typeof ticketFiltersValidator>;
